var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { IconCaretDown } from '@hawkins/icons/lib/iconCaretDown';
import { IconCaretUp } from '@hawkins/icons/lib/iconCaretUp';
import { IconClose } from '@hawkins/icons/lib/iconClose';
import { IconLock } from '@hawkins/icons/lib/iconLock';
import { spacings } from '@hawkins/variables';
import { Box } from '../../../box';
import { Button } from '../../../button';
import { FloatingUI } from '../../../floatingUi';
import { useClickOutside, useCombineRefs } from '../../../hooks';
import { Loader } from '../../../loader';
import { Text } from '../../../text';
import { DEFAULT_LIST_MIN_WIDTH, DROPDOWN_LIST_WRAPPER_HEIGHT } from '../../dropdown.constants';
import { MenuGroupLabel } from '../../menuGroupLabel';
import { AugmentedFooterWithCreatable } from './augmentedFooterWithCreatable';
/**
 * This file contains default components for some parts of the dropdown
 */
/**
 * Gets the default end adornment for the dropdown toggle
 * This component is used privately and cannot be overwritten by the user
 * @param props
 * @returns React.Element
 */
export function EndAdornment(props) {
    var disabled = props.disabled, hasSelected = props.hasSelected, isOpen = props.isOpen, clearable = props.clearable, clearButtonClassName = props.clearButtonClassName, dividerClassName = props.dividerClassName, onClearInput = props.onClearInput;
    var icon;
    if (disabled) {
        icon = _jsx(IconLock, { size: "small" });
    }
    else if (isOpen) {
        icon = _jsx(IconCaretUp, { style: { cursor: 'pointer' }, size: "small" });
    }
    else {
        icon = _jsx(IconCaretDown, { style: { cursor: 'pointer' }, size: "small" });
    }
    var showClearButton = clearable && hasSelected;
    return (_jsxs(_Fragment, { children: [showClearButton && (_jsx(Button, { variant: "icon", "aria-label": "clear", compact: true, icon: IconClose, className: clearButtonClassName, onClick: onClearInput, "data-hawkins-style": "Hawkins-Dropdown-clearButton" })), _jsx("div", { className: dividerClassName, "data-hawkins-style": "Hawkins-Dropdown-divider" }), icon] }));
}
EndAdornment.displayName = 'EndAdornment';
/**
 * Default component for the dropdown list group header
 * We currently use the MenuGroupLabel as the default - This component will only be used
 * if user specifies the sortBy function in the props
 * @param props ListGroupHeader<DropdownOptionType, RawValueType>
 * @returns React.ReactElement
 */
// eslint-disable-next-line react/display-name
export function DropdownListGroupHeaderDefault(props) {
    var _a;
    return (_jsx(MenuGroupLabel, { label: props.children, className: clsx((_a = props.classes) === null || _a === void 0 ? void 0 : _a.groupLabel, 'Hawkins-dropdown-group-label') }, "dropdown-group-label-".concat(props.optionKey)));
}
/**
 * Default component for the dropdown list wrapper
 * We currently use the FloatingUI as the default list wrapper
 * @param props ListWrapperProps<DropdownOptionType, RawValueType>
 * @returns React.ReactElement | null
 */
// eslint-disable-next-line react/display-name
export function DropdownListWrapperDefault(props) {
    // props
    var autoComplete = props.autoComplete, children = props.children, className = props.className, disableFlip = props.disableFlip, disablePortal = props.disablePortal, dropdownWidthType = props.dropdownWidthType, id = props.id, listRef = props.listRef, loading = props.loading, onOutsideClick = props.onOutsideClick, options = props.options, open = props.open, position = props.position, triggerRef = props.triggerRef;
    // hooks
    var outsideClickRef = useClickOutside({
        onOutsideClick: onOutsideClick,
    });
    // refs
    var combinedFloatingUIRefsForDropdown = useCombineRefs([listRef, outsideClickRef]);
    // memos
    var floatingStyle = React.useMemo(function () {
        var _a;
        return ({
            display: 'flex',
            flexDirection: 'column',
            minWidth: DEFAULT_LIST_MIN_WIDTH,
            position: (_a = props.dropdownListPosition) !== null && _a !== void 0 ? _a : 'fixed',
        });
    }, [props.dropdownListPosition]);
    var floatingUIProps = React.useMemo(function () {
        var _a;
        return (__assign(__assign({}, props.floatingUIProps), { strategy: (_a = props.dropdownListPosition) !== null && _a !== void 0 ? _a : 'fixed' }));
    }, [props.floatingUIProps, props.dropdownListPosition]);
    if (autoComplete && (options === null || options === void 0 ? void 0 : options.length) === 0 && !loading)
        return null;
    return (_jsx(FloatingUI, __assign({ anchor: triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current, className: className, disableFlip: disableFlip, disablePortal: disablePortal, floatingUIProps: floatingUIProps, id: id, maxHeight: DROPDOWN_LIST_WRAPPER_HEIGHT, open: open, placement: position, ref: combinedFloatingUIRefsForDropdown, style: floatingStyle, styled: false, useFocusManager: true, widthVariant: dropdownWidthType }, { children: children })));
}
/**
 * Default Loading component for when there are items being loading in the dropdown list
 * We currently use Hawkins Loader with variant 'linear' as default
 * @returns React.ReactElement
 */
// eslint-disable-next-line react/display-name
export function DropdownListLoadingDefault() {
    return (_jsx(Box, __assign({ width: "100%" }, { children: _jsx(Loader, { variant: "linear" }) })));
}
/**
 * Default component for when there are no search results found in the dropdown list
 * We currently use Hawkins Loader with variant 'linear' as default
 * @param props DropdownProps<DropdownOptionType, RawValueType>
 * @returns React.ReactElement
 */
// eslint-disable-next-line react/display-name
export function DropdownListEmptySuggestionDefault(props) {
    return (_jsx(Box, __assign({ paddingLeft: spacings.space3, paddingRight: spacings.space3, display: "flex", alignItems: "center", justifyContent: "center" }, { children: _jsx(Text, __assign({ variant: "body", gutterBottom: spacings.space1, gutterTop: spacings.space1 }, { children: props.emptyLabel })) })));
}
/**
 * Default component for the list footer label
 * @param props DropdownProps<DropdownOptionType, RawValueType>
 * @returns React.ReactElement
 */
// eslint-disable-next-line react/display-name
export function DropdownListFooterLabelDefault() {
    return null;
}
/**
 * Default component for the list footer when it is not creatable
 * @param props DropdownProps<DropdownOptionType, RawValueType>
 * @returns React.ReactElement
 */
// eslint-disable-next-line react/display-name
export function DropdownListFooterDefault(props) {
    var DropdownListFooterLabel = props.DropdownListFooterLabel;
    if (!DropdownListFooterLabel) {
        return null;
    }
    return (_jsx(Box, __assign({ paddingLeft: spacings.space3, paddingRight: spacings.space3 }, { children: _jsx(DropdownListFooterLabel, __assign({}, props)) })));
}
/**
 * Default component for the dropdown list footer
 * Our current default footer is the Creatable footer
 * @param props ListFooterProps<DropdownOptionType, RawValueType>
 * @returns React.ReactElement | null
 */
// eslint-disable-next-line react/display-name
export function DropdownFooterDefault(props) {
    return (_jsx("div", __assign({ role: "contentinfo" }, { children: _jsx(AugmentedFooterWithCreatable, __assign({}, props, { query: props.query, footerRef: props.footerRef, createOptionLabel: props.createOptionLabel, onCreateOption: props.onCreateOption })) })));
}
